import { MAX_AUTH_REPLAY_ATTEMPTS } from '../constants/auth-replay-constants'
import type {
  AuthReplayErrorContext,
  AuthReplaySessionInput,
  AuthReplayUnauthorizedRecord,
} from '../types/auth-replay-types'
import { AuthRefreshFailedError } from './AuthRefreshFailedError'
import { AuthReplayLimitExceededError } from './AuthReplayLimitExceededError'

/**
 * 单个原始请求的鉴权重放会话。
 *
 * 设计说明：计数必须跟随原始请求事务，而不是全局共享；否则一个下载文件的 401
 * 会错误影响其它正在进行的模型文件或 RPC 请求。
 */
export class AuthReplaySession {
  /** 请求稳定上下文。 */
  private readonly input: AuthReplaySessionInput
  /** 当前原始请求已连续触发的 unauthorized 次数。 */
  private unauthorizedCount = 0
  /** 最近一次 unauthorized 记录，用于超限错误定位。 */
  private lastUnauthorizedRecord: AuthReplayUnauthorizedRecord | null = null

  /**
   * 创建鉴权重放会话。
   *
   * @param input 请求入口和定位信息。
   */
  public constructor(input: AuthReplaySessionInput) {
    this.input = input
  }

  /**
   * 读取当前连续 unauthorized 次数。
   *
   * @returns 连续 unauthorized 次数。
   */
  public get attemptCount(): number {
    return this.unauthorizedCount
  }

  /**
   * 记录一次 unauthorized，并在超过上限时抛出结构化错误。
   *
   * @param record 本次 unauthorized 的状态和原始错误。
   */
  public recordUnauthorizedAndAssertCanReplay(record: AuthReplayUnauthorizedRecord): void {
    this.unauthorizedCount += 1
    this.lastUnauthorizedRecord = record

    if (this.unauthorizedCount > MAX_AUTH_REPLAY_ATTEMPTS) {
      throw new AuthReplayLimitExceededError(this.createErrorContext(record.cause))
    }
  }

  /**
   * 执行调用方注入的 refresh，并把 refresh 失败包装成稳定错误。
   *
   * @param refreshAuthorization 实际刷新 token 或 Authorization header 的函数。
   * @returns refresh 函数返回的新鉴权值。
   */
  public async refreshAuthorization<T>(refreshAuthorization: () => T | Promise<T>): Promise<T> {
    try {
      return await refreshAuthorization()
    } catch (error) {
      throw new AuthRefreshFailedError(this.createErrorContext(error))
    }
  }

  /**
   * 创建结构化错误上下文。
   *
   * @param cause 原始错误。
   * @returns 可供错误对象使用的上下文。
   */
  private createErrorContext(cause: unknown): AuthReplayErrorContext {
    return {
      ...this.input,
      attemptCount: this.unauthorizedCount,
      status: this.lastUnauthorizedRecord?.status,
      code: this.lastUnauthorizedRecord?.code,
      cause,
    }
  }
}
